import { compareHuge, huge, type HugeNumberInput } from "@/shared/numbers";
import type { MpmState } from "../mpm/mpmState";
import {
  getExplorationAssignedMpByBiome,
  getExplorationMpSummary,
  resolveAssignExplorationMpSlot,
  resolvePurchaseExplorationMpSlot,
  resolveRemoveExplorationMpSlot,
} from "./explorationCommands";
import { cloneExplorationState } from "./explorationState";
import {
  EXPLORATION_BIOME_IDS,
  type ExplorationAreaDetailsSnapshot,
  type ExplorationAreaSnapshot,
  type ExplorationBiomeId,
  type ExplorationDetailsSnapshot,
  type ExplorationDefinition,
  type ExplorationMpSlotDetailsSnapshot,
  type ExplorationOperationAvailability,
  type ExplorationPurchasedMpSlotSnapshot,
  type ExplorationSnapshot,
  type ExplorationState,
} from "./explorationTypes";

export function createExplorationSnapshot(input: {
  readonly state: ExplorationState;
  readonly definition: ExplorationDefinition;
  readonly mpmState: MpmState;
  readonly totalMpCapacity: number;
  readonly pointerBiomeId?: ExplorationBiomeId | null;
}): ExplorationSnapshot {
  const state = cloneExplorationState(input.state);
  return createBaseSnapshot({ ...input, state });
}

export function createExplorationDetailsSnapshot(input: {
  readonly state: ExplorationState;
  readonly definition: ExplorationDefinition;
  readonly mpmState: MpmState;
  readonly totalMpCapacity: number;
  readonly availableHovCore: HugeNumberInput;
  readonly availableTim: HugeNumberInput;
  readonly pointerBiomeId?: ExplorationBiomeId | null;
  readonly timAccelerationUnlocked: boolean;
}): ExplorationDetailsSnapshot {
  const state = cloneExplorationState(input.state);
  const base = createBaseSnapshot({ ...input, state });
  const areas = Object.fromEntries(
    EXPLORATION_BIOME_IDS.map((biomeId) => {
      const definition = input.definition.areas[biomeId];
      const area: ExplorationAreaDetailsSnapshot = {
        ...base.areas[biomeId],
        isRunning: base.areas[biomeId].participation > 0,
        currentEffect: definition.resolveEffect(state.biomeLevel),
        mpSlots: base.purchasedMpSlots
          .filter((slot) => slot.biomeId === biomeId)
          .map((slot) =>
            createSlotDetails({
              slot,
              state,
              definition: input.definition,
              mpmState: input.mpmState,
              totalMpCapacity: input.totalMpCapacity,
            }),
          ),
      };
      return [biomeId, area];
    }),
  ) as Record<ExplorationBiomeId, ExplorationAreaDetailsSnapshot>;

  const nextSlot = input.definition.mpSlots[state.purchasedMpSlotCount] ?? null;
  const nextCost =
    nextSlot === null
      ? null
      : (input.definition.mpSlotCosts[state.purchasedMpSlotCount] ?? null);

  return {
    ...base,
    biomeIds: [...EXPLORATION_BIOME_IDS],
    areas,
    nextMpSlotPurchase: {
      slot: nextSlot,
      cost: nextCost,
      availability: toAvailability(
        resolvePurchaseExplorationMpSlot({
          state,
          definition: input.definition,
          availableHovCore: input.availableHovCore,
        }),
      ),
    },
    timAcceleration: createTimAccelerationSnapshot({
      state,
      definition: input.definition,
      availableTim: input.availableTim,
      unlocked: input.timAccelerationUnlocked,
      hasParticipation: EXPLORATION_BIOME_IDS.some(
        (biomeId) => areas[biomeId].participation > 0,
      ),
    }),
  };
}

function createBaseSnapshot(input: {
  readonly state: ExplorationState;
  readonly definition: ExplorationDefinition;
  readonly mpmState: MpmState;
  readonly totalMpCapacity: number;
  readonly pointerBiomeId?: ExplorationBiomeId | null;
}): ExplorationSnapshot {
  const assignedSlotIds = new Set(input.mpmState.exploration.assignedSlotIds);
  const purchasedMpSlots: ExplorationPurchasedMpSlotSnapshot[] =
    input.definition.mpSlots
      .slice(0, input.state.purchasedMpSlotCount)
      .map((slot) => ({
        ...slot,
        assigned: assignedSlotIds.has(slot.id),
      }));
  const assignedByBiome = getExplorationAssignedMpByBiome({
    definition: input.definition,
    mpmState: input.mpmState,
  });
  const areas = Object.fromEntries(
    EXPLORATION_BIOME_IDS.map((biomeId) => {
      const areaState = input.state.areas[biomeId];
      const periodMs = input.definition.areas[biomeId].periodMs;
      const area: ExplorationAreaSnapshot = {
        id: biomeId,
        ...areaState,
        assignedMp: assignedByBiome[biomeId],
        pointerParticipation: input.pointerBiomeId === biomeId ? 1 : 0,
        participation:
          assignedByBiome[biomeId] + (input.pointerBiomeId === biomeId ? 1 : 0),
        periodMs,
        remainingMs: Math.max(0, periodMs - areaState.progressMs),
      };
      return [biomeId, area];
    }),
  ) as Record<ExplorationBiomeId, ExplorationAreaSnapshot>;
  return {
    biomeLevel: input.state.biomeLevel,
    purchasedMpSlotCount: input.state.purchasedMpSlotCount,
    cumulativeTimAddedMs: input.state.cumulativeTimAddedMs,
    purchasedMpSlots,
    areas,
    mp: getExplorationMpSummary({
      mpmState: input.mpmState,
      totalMpCapacity: input.totalMpCapacity,
    }),
  };
}

function createTimAccelerationSnapshot(input: {
  readonly state: ExplorationState;
  readonly definition: ExplorationDefinition;
  readonly availableTim: HugeNumberInput;
  readonly unlocked: boolean;
  readonly hasParticipation: boolean;
}): ExplorationDetailsSnapshot["timAcceleration"] {
  const enabled = input.unlocked && input.state.timAccelerationEnabled;
  const hasTim = compareHuge(huge(input.availableTim), 0) > 0;
  const isApplied = enabled && input.hasParticipation && hasTim;
  const status = !input.unlocked
    ? "locked"
    : !enabled || !input.hasParticipation
      ? "stopped"
      : hasTim
        ? "running"
        : "insufficient-tim";
  return {
    unlocked: input.unlocked,
    enabled,
    isApplied,
    status,
    timPerGameSecond: input.definition.timAcceleration.timPerGameSecond,
    speedMultiplier: input.definition.timAcceleration.speedMultiplier,
    currentTimPerGameSecond: isApplied
      ? input.definition.timAcceleration.timPerGameSecond
      : 0,
    currentSpeedMultiplier: isApplied
      ? input.definition.timAcceleration.speedMultiplier
      : 1,
  };
}

function createSlotDetails(input: {
  readonly slot: ExplorationPurchasedMpSlotSnapshot;
  readonly state: ExplorationState;
  readonly definition: ExplorationDefinition;
  readonly mpmState: MpmState;
  readonly totalMpCapacity: number;
}): ExplorationMpSlotDetailsSnapshot {
  return {
    ...input.slot,
    pausedByMpm: input.slot.assigned && !input.mpmState.exploration.enabled,
    hiddenByMpm: !input.slot.assigned && !input.mpmState.exploration.enabled,
    assignAvailability: toAvailability(
      resolveAssignExplorationMpSlot({
        state: input.state,
        definition: input.definition,
        mpmState: input.mpmState,
        totalMpCapacity: input.totalMpCapacity,
        slotId: input.slot.id,
      }),
    ),
    removeAvailability: toAvailability(
      resolveRemoveExplorationMpSlot({
        mpmState: input.mpmState,
        slotId: input.slot.id,
      }),
    ),
  };
}

function toAvailability(
  result:
    | ReturnType<typeof resolvePurchaseExplorationMpSlot>
    | ReturnType<typeof resolveAssignExplorationMpSlot>
    | ReturnType<typeof resolveRemoveExplorationMpSlot>,
): ExplorationOperationAvailability {
  if (!result.ok) {
    return { canExecute: false, reason: result.reason };
  }
  return "cost" in result
    ? { canExecute: true, cost: result.cost }
    : { canExecute: true };
}
